import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";

const DEFAULT = {
  eyebrow: "Our Story",
  heading: "Made with a love for the city we call home.",
  body:
    "Memphis Coffee Co. started with a cooler, a few bottles, and a simple idea: serve delicious coffee to everyday people around the city. Parks, pop-ups, neighborhoods, and wherever Memphis gathers.",
  image_url:
    "https://images.unsplash.com/photo-1507915135761-41a0a222c709?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxiYXJpc3RhJTIwcG91cmluZyUyMGVzcHJlc3NvfGVufDB8fHx8MTc4MDg5Njg1Nnww&ixlib=rb-4.1.0&q=85",
  stats: [
    { id: "s1", label: "Bottled in", value: "Memphis, TN" },
    { id: "s2", label: "Pop-ups", value: "Every weekend" },
    { id: "s3", label: "Ingredients", value: "Kept simple" },
  ],
};

export const About = () => {
  const [data, setData] = useState(DEFAULT);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/settings");
        if (!cancelled && r.data?.about) setData(r.data.about);
      } catch {
        /* keep defaults */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const stats = Array.isArray(data.stats) && data.stats.length ? data.stats : DEFAULT.stats;

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-32 bg-[#F3EDE4]/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="md:col-span-6 relative"
        >
          <div className="aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#E8E3DD]">
            {data.image_url ? (
              <img
                data-testid="about-image"
                src={data.image_url}
                alt={data.heading || "About"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#8D99AE] text-sm">
                No image set
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
          className="md:col-span-6"
        >
          {data.eyebrow && (
            <div data-testid="about-eyebrow" className="text-xs uppercase tracking-[0.18em] text-[#8D99AE] mb-4">
              {data.eyebrow}
            </div>
          )}
          <h2
            data-testid="about-heading"
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#2C1E16] leading-tight"
          >
            {data.heading}
          </h2>
          {data.body && (
            <p data-testid="about-body" className="mt-6 text-base sm:text-lg leading-relaxed text-[#5C4E46] whitespace-pre-wrap">
              {data.body}
            </p>
          )}
          <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4 text-[#5C4E46]">
            {stats.map((s) => (
              <Stat key={s.id || `${s.label}-${s.value}`} label={s.label} value={s.value} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const Stat = ({ label, value }) => (
  <div data-testid={`about-stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className="text-[11px] uppercase tracking-[0.18em] text-[#8D99AE]">{label}</div>
    <div className="font-display text-lg text-[#2C1E16] mt-1">{value}</div>
  </div>
);
